import React, { useState, useEffect } from 'react';
import { ShieldAlert, KeyRound, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BETA_PASSWORD = 'baylash1881';
const STORAGE_KEY = 'beta_access_granted';

interface BetaAccessGuardProps {
  children: React.ReactNode;
}

export const BetaAccessGuard: React.FC<BetaAccessGuardProps> = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  useEffect(() => {
    setIsAuthorized(false);
  }, []);

  useEffect(() => {
    if (!isAuthorized) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isAuthorized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === BETA_PASSWORD) {
      setIsAuthorized(true);
      setError('');
    } else {
      setError('Hatalı şifre! Lütfen tekrar deneyiniz.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleGoHome = () => {
    window.location.href = '/app';
  };

  return (
    <>
      <div className={!isAuthorized ? 'filter blur-md pointer-events-none select-none transition-all duration-300' : ''}>
        {children}
      </div>
      <AnimatePresence>
        {!isAuthorized && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-xl select-none"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{
                scale: 1,
                y: 0,
                opacity: 1,
                x: shake ? [-10, 10, -10, 10, 0] : 0,
              }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md bg-slate-900/90 backdrop-blur-2xl border border-amber-500/40 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-amber-500/10 text-white relative overflow-hidden"
            >
              {/* Ambient glow decoration */}
              <div className="absolute -top-16 -left-16 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -right-16 w-32 h-32 bg-amber-600/20 rounded-full blur-3xl pointer-events-none" />

              {/* Beta Warning Badge */}
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold tracking-wider uppercase">
                  <ShieldAlert className="w-4 h-4 animate-pulse" />
                  <span>Beta Sürüm Uyarısı</span>
                </div>
              </div>

              {/* Title & Description */}
              <div className="text-center space-y-2 mb-6">
                <h2 className="text-2xl font-bold tracking-tight text-slate-100 flex items-center justify-center gap-2">
                  <Lock className="w-6 h-6 text-amber-400" />
                  Erişim Korumalı
                </h2>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Steel Coil Hesaplayıcı şu anda <span className="text-amber-400 font-semibold">BETA</span> aşamasındadır. Kullanıma devam edebilmek için lütfen erişim şifresini giriniz.
                </p>
              </div>

              {/* Password Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-400">
                    Beta Erişim Şifresi
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <KeyRound className="w-4 h-4" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="Şifrenizi giriniz..."
                      autoFocus
                      className="w-full pl-10 pr-10 py-3 bg-slate-800/80 border border-slate-700 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </motion.div>
                )}

                {/* Submit & Navigation Buttons */}
                <div className="space-y-2 pt-1">
                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold rounded-xl text-sm shadow-lg shadow-amber-500/20 transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Giriş Yap ve Devam Et
                  </button>

                  <button
                    type="button"
                    onClick={handleGoHome}
                    className="w-full py-2.5 px-4 bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/80 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Kapat ve Uygulamaya Git (/app)
                  </button>
                </div>
              </form>

              {/* Bottom Footer Note */}
              <div className="mt-6 pt-4 border-t border-slate-800 text-center">
                <p className="text-[11px] text-slate-500">
                  Lashing Calculator Platform &copy; Beta Version Guard
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
